import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useNav from "./hooks/useNav";
import { CheckCircle, MapPin, FileText, Home, ClipboardList, AlertCircle, CreditCard } from "lucide-react";
import PrimaryButton from "./components/PrimaryButton";
import { useAuthFetch } from "./hooks/useAuthFetch";
import { useRestaurant } from "./context/RestaurantContext";
import appConfig from "./config";
import { toast } from "react-toastify";
import { useCart } from "./context/CartContext";

const OrderSuccessPage = () => {
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(true);

    const navigate = useNav();
    const location = useLocation();
    const { authFetch } = useAuthFetch();
    const { restaurant } = useRestaurant();
    const { clearCart } = useCart();

    const { order, paymentMethod, paymentDetails } = location.state || {};

    useEffect(() => {
        if (!order) {
            toast.error("Данные заказа не найдены");
            navigate("/");
            return;
        }

        clearCart();
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred("success");
        fetchOrderDetails();
    }, [order]);

    const fetchOrderDetails = async () => {
        try {
            const response = await authFetch(`${appConfig.endpoint}/orders/${order.id}/view`);
            if (response.success) {
                setOrderDetails(response.order);
            } else {
                throw new Error(response.error || "Ошибка загрузки данных заказа");
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
            toast.error("Не удалось загрузить детали заказа");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brandBlue" />
            </div>
        );
    }

    const orderInfo = orderDetails || order;
    const isAwaitingPayment = paymentMethod !== "cash" && !paymentDetails?.transactionId;

    const handleRetryPayment = () => {
        navigate(`/payment-processing?slug=${restaurant?.slug || ""}`, {
            state: {
                order: orderInfo,
                paymentMethod: paymentMethod || "card",
                restaurantId: orderInfo.restaurantID,
            },
        });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-screen-sm mx-auto px-4 pt-10 pb-32">

                {/* Header */}
                <div className="flex flex-col items-center text-center mb-8">
                    {restaurant?.logo ? (
                        <div className="relative mb-5">
                            <div className="absolute inset-0 -m-4 rounded-full bg-brandBlue/8" />
                            <img
                                src={restaurant.logo}
                                alt={restaurant.name}
                                className="relative h-24 w-auto max-w-[180px] object-contain"
                                style={{ filter: 'drop-shadow(0 4px 16px rgba(62,125,174,0.2))' }}
                                onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
                            />
                        </div>
                    ) : (
                        <div
                            className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
                            style={{ background: isAwaitingPayment ? "rgb(254 252 232)" : "rgb(240 253 244)" }}
                        >
                            {isAwaitingPayment
                                ? <AlertCircle size={40} className="text-yellow-500" strokeWidth={1.5} />
                                : <CheckCircle size={40} className="text-green-500" strokeWidth={1.5} />
                            }
                        </div>
                    )}
                    <h1 className="font-playfair text-2xl font-semibold text-gray-900 tracking-wide">
                        {restaurant?.name || "Заказ оформлен"}
                    </h1>
                    <div className="mt-2 mb-3 w-10 h-0.5 rounded-full bg-brandBlue/50" />
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-2 ${isAwaitingPayment ? "bg-yellow-50 text-yellow-600" : "bg-green-50 text-green-600"}`}>
                        {isAwaitingPayment
                            ? <AlertCircle size={12} strokeWidth={2.5} />
                            : <CheckCircle size={12} strokeWidth={2.5} />
                        }
                        {isAwaitingPayment ? "Ожидает оплаты" : "Заказ оформлен"}
                    </span>
                    <p className="text-gray-400 text-sm">
                        Заказ <span className="font-mono font-semibold text-gray-600">#{orderInfo.id}</span>
                    </p>
                </div>

                {/* Summary card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-brandBlue/5 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500 mb-1">Сумма</p>
                            <p className="text-xl font-bold text-brandBlue">{orderInfo.amount} ₽</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500 mb-1">Оплата</p>
                            <p className="text-base font-semibold text-gray-800">
                                {paymentMethod === "cash" ? "Наличными" : paymentMethod === "card" ? "Картой" : "Онлайн"}
                            </p>
                            {paymentMethod === "cash" && (
                                <p className="text-xs text-gray-400 mt-0.5">При получении</p>
                            )}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                <MapPin size={16} className="text-gray-400" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-gray-800">Получение</p>
                                <p className="text-sm text-gray-500">
                                    {orderInfo.delivery ? "Доставка" : "Самовывоз из ресторана"}
                                </p>
                            </div>
                        </div>

                        {paymentDetails?.transactionId && (
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                    <FileText size={16} className="text-gray-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-800">Транзакция</p>
                                    <p className="text-sm text-gray-500 font-mono">{paymentDetails.transactionId}</p>
                                </div>
                            </div>
                        )}

                        {orderInfo.note && (
                            <div className="pt-3 border-t border-gray-100">
                                <p className="text-xs text-gray-400 mb-1">Комментарий</p>
                                <p className="text-sm text-gray-600">{orderInfo.note}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* What's next */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <p className="text-sm font-semibold text-gray-700 mb-4">Что дальше?</p>
                    <div className="space-y-3">
                        {(isAwaitingPayment
                            ? [
                                "Нажмите кнопку «Оплатить» и завершите оплату",
                                "После оплаты заказ будет автоматически передан на кухню",
                            ]
                            : [
                                "Заказ принят и передан на кухню",
                                orderInfo.delivery
                                    ? "Курьер свяжется с вами для уточнения деталей"
                                    : "Подойдите к стойке и назовите номер заказа",
                            ]
                        ).map((text, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-brandBlue/10 text-brandBlue flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                                    {i + 1}
                                </div>
                                <p className="text-sm text-gray-600 pt-0.5">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Fixed bottom */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 shadow-lg">
                <div className="max-w-screen-sm mx-auto flex flex-col gap-2">
                    {isAwaitingPayment && (
                        <PrimaryButton
                            onClick={handleRetryPayment}
                            className="w-full flex items-center justify-center gap-2 py-3"
                        >
                            <CreditCard size={18} className="shrink-0" />
                            Оплатить заказ
                        </PrimaryButton>
                    )}
                    <div className="flex gap-3">
                        <PrimaryButton
                            onClick={() => navigate("/")}
                            className="flex-1 flex items-center justify-center gap-2 py-3"
                        >
                            <Home size={18} className="shrink-0" />
                            Главная
                        </PrimaryButton>
                        <button
                            onClick={() => navigate("/history")}
                            className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-2xl font-medium text-sm active:scale-95 transition-all"
                        >
                            <ClipboardList size={18} className="shrink-0" />
                            Мои заказы
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderSuccessPage;